/**
 * ResetPasswordPage — redeems the one-time token from the password-reset
 * email (/reset-password?token=…) and lets the user set a new password.
 *
 * Companion to the SEC fix that removed the reset token from the
 * /auth/forgot-password response: the emailed link is now the ONLY way a
 * token reaches a user, and this page is where it lands. Mirrors
 * InviteRegisterPage (same card chrome, password policy meter, and
 * confirm-match affordances) so the two token-redemption surfaces feel
 * identical.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Check, AlertTriangle, Lock, KeyRound } from 'lucide-react';
import * as auth from '../services/auth';
import { validatePassword, getPasswordRequirements, getPasswordStrength, PASSWORD_STRENGTH_LABELS, PASSWORD_STRENGTH_COLORS } from '../utils/passwordPolicy';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  /* Narrow-viewport breakpoint. Same shape as LoginPage:102-110 (and
   * DashboardLayoutV2 / CanvasMobileGate) so every surface agrees on where
   * "phone" begins: matchMedia held in state, kept current by a change
   * listener. Guarded for environments without matchMedia (SSR, jsdom), where
   * it simply stays false and the desktop sizing applies. */
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(max-width: 767px)').matches
  );
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const pwResult = validatePassword(password);
    if (!pwResult.valid) {
      setSubmitError(pwResult.errors[0]);
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    const result = await auth.resetPassword(token, password);
    if (result.ok) {
      setSuccess(true);
    } else {
      setSubmitError(result.error || 'Reset failed — the link may have expired. Request a new one from the sign-in page.');
    }
    setSubmitting(false);
  };

  const strength = getPasswordStrength(password);

  // ─── Shared styles (mirrors InviteRegisterPage) ───────────────────────────
  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fafaf9 0%, #f5f5f4 50%, #e3f4f1 100%)',
    display: 'flex',
    // Column so the brand lockup stacks ABOVE the card rather than beside it.
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    padding: isNarrow ? 16 : 24,
  };

  const cardStyle = {
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    width: '100%',
    maxWidth: 460,
    padding: isNarrow ? 24 : 40,
    border: '1px solid #e7e5e4',
  };

  /* Brand lockup — stacked mark over wordmark with the product line, sitting
   * ABOVE the card on the gradient so the page is recognisably Prove7 before a
   * word of copy is read. Dark variants because this surface is light; the
   * -light pair exists for LoginPage's navy hero. The mark carries the
   * accessible name (the same string its sibling pages use —
   * InviteRegisterPage:182, SsoCallbackPage:114); the wordmark repeats it
   * visually and is marked decorative so a screen reader says the brand once. */
  const brandLockup = (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 9, marginBottom: isNarrow ? 20 : 24,
    }}>
      <img
        src="/brand/mark-dark.png"
        alt="Prove7 Control Vector Platform"
        style={{ width: 44, height: 'auto', display: 'block' }}
      />
      <img
        src="/brand/wordmark-dark.png"
        alt=""
        style={{ width: isNarrow ? 96 : 108, height: 'auto', display: 'block' }}
      />
      <div style={{
        fontSize: 9, fontWeight: 700, letterSpacing: '0.17em',
        textTransform: 'uppercase', color: '#78716c',
      }}>
        Control Vector
      </div>
    </div>
  );

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #e4e4e7',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#52525b',
    marginBottom: 6,
  };

  // ─── Missing token ────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div style={containerStyle}>
        {brandLockup}
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fef2f2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <AlertTriangle size={24} color="#ef4444" />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#18181b', margin: '0 0 8px 0' }}>Invalid Reset Link</h2>
            <p style={{ fontSize: 14, color: '#71717a', margin: '0 0 24px 0' }}>
              No reset token provided. Please use the link from your password-reset email, or request a new one.
            </p>
            <button onClick={() => navigate('/login')} style={{
              padding: '10px 24px', borderRadius: 8, border: 'none',
              background: '#18181b', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Success ──────────────────────────────────────────────────────────────
  if (success) {
    return (
      <div style={containerStyle}>
        {brandLockup}
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e3f4f1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <Check size={28} color="#0d9488" />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#18181b', margin: '0 0 8px 0' }}>Password Updated</h2>
            <p style={{ fontSize: 14, color: '#71717a', margin: '0 0 8px 0' }}>
              Your password has been reset successfully.
            </p>
            <p style={{ fontSize: 13, color: '#a8a29e', margin: '0 0 24px 0' }}>
              All existing sessions have been signed out. Sign in with your new password.
            </p>
            <button onClick={() => navigate('/login')} style={{
              padding: '12px 32px', borderRadius: 8, border: 'none',
              background: '#0d9488', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Reset form ───────────────────────────────────────────────────────────
  return (
    <div style={containerStyle}>
      {brandLockup}
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 8, background: '#e3f4f1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <KeyRound size={24} color="#0d9488" />
            </div>
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#18181b', margin: '0 0 6px 0' }}>
            Choose a New Password
          </h1>
          <p style={{ fontSize: 14, color: '#71717a', margin: 0 }}>
            This link can be used once and expires quickly.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>
                <Lock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ ...inputStyle, paddingRight: 40 }}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                  autoFocus
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#a8a29e', padding: 4 }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {password && (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 3, flex: 1 }}>
                    {[1,2,3,4].map((i) => (
                      <div key={i} style={{
                        flex: 1, height: 4, borderRadius: 2,
                        background: strength >= i ? PASSWORD_STRENGTH_COLORS[strength] : '#e4e4e7',
                        transition: 'background 0.2s',
                      }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 11, color: PASSWORD_STRENGTH_COLORS[strength], fontWeight: 600, minWidth: 44 }}>
                    {PASSWORD_STRENGTH_LABELS[strength]}
                  </span>
                </div>
              )}
              <div style={{ fontSize: 11, color: '#a8a29e', marginTop: 4 }}>
                {getPasswordRequirements()}
              </div>
            </div>

            <div>
              <label style={labelStyle}>Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    ...inputStyle,
                    paddingRight: 40,
                    borderColor: confirmPassword && confirmPassword !== password ? '#ef4444' : confirmPassword && confirmPassword === password ? '#0d9488' : '#e4e4e7',
                  }}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#a8a29e', padding: 4 }}>
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {confirmPassword && confirmPassword !== password && (
                <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Passwords do not match</div>
              )}
              {confirmPassword && confirmPassword === password && (
                <div style={{ fontSize: 11, color: '#0d9488', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Check size={12} /> Passwords match
                </div>
              )}
            </div>

            {submitError && (
              <div style={{
                padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 8, fontSize: 13, color: '#991b1b',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <AlertTriangle size={14} />
                {submitError}
              </div>
            )}

            <button type="submit" disabled={submitting || !password || !confirmPassword || password !== confirmPassword}
              style={{
                padding: '12px 24px', borderRadius: 8, border: 'none',
                background: submitting || !password || !confirmPassword || password !== confirmPassword ? '#d6d3d1' : '#0d9488',
                color: '#ffffff', fontSize: 14, fontWeight: 600, cursor: submitting ? 'wait' : 'pointer',
                transition: 'background 0.2s', marginTop: 8,
              }}>
              {submitting ? 'Updating Password...' : 'Reset Password'}
            </button>
          </div>
        </form>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button onClick={() => navigate('/login')}
            style={{ background: 'none', border: 'none', color: '#a8a29e', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}
